import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories } from "@/mocks/homeContent";

export default function ArticlePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL;
  const pagePath = "/allchinabuyspreadsheet-guide";

  return (
    <div className="min-h-screen bg-background-50">
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "AllChinaBuySpreadsheet Guide – How to Source Products from China Efficiently",
            description:
              "A comprehensive guide to using AllChinaBuySpreadsheet for China product sourcing. Learn how to find verified suppliers, compare prices, and reduce buying risks.",
            author: { "@type": "Organization", name: "AllChinaBuy" },
            datePublished: "2026-06-25",
            dateModified: "2026-06-25",
            publisher: { "@type": "Organization", name: "AllChinaBuy", url: siteUrl },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${siteUrl}${pagePath}` },
          }),
        }}
      />
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="relative pt-24 pb-12 md:pt-28 md:pb-16 bg-background-100/70">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors mb-6 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              AllChinaBuySpreadsheet Guide: How to Source Products from China Efficiently
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                Updated: June 2026
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                8 min read
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-price-tag-3-line"></i>
                China Sourcing, Spreadsheet Guide
              </span>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="relative py-10 md:py-14">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <div className="prose-custom text-foreground-800 leading-relaxed space-y-8">
              {/* Intro */}
              <section>
                <p className="text-base md:text-lg">
                  Sourcing products from China can feel like navigating a maze without a map. Between
                  countless supplier listings, fluctuating prices, and quality concerns, even experienced
                  buyers find themselves spending hours just trying to compare options. That is exactly
                  why the <strong>AllChinaBuySpreadsheet</strong> was created — to turn scattered product
                  data into a clean, structured, and actionable sourcing tool.
                </p>
                <p className="text-base md:text-lg mt-4">
                  In this guide, we will walk through everything you need to know about using the
                  AllChinaBuySpreadsheet database effectively. Whether you are a first-time dropshipper,
                  a seasoned reseller, or a sneaker enthusiast hunting for the best batches, this resource
                  is designed to save you time, money, and headaches.
                </p>
              </section>

              {/* Section 1 */}
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  What Is AllChinaBuySpreadsheet?
                </h2>
                <p className="text-base">
                  The AllChinaBuySpreadsheet is a comprehensive, regularly updated database that aggregates
                  product information from major Chinese marketplaces including Taobao, 1688, Weidian, and
                  other supplier platforms. Instead of jumping between dozens of browser tabs, you get a
                  single interface where products are organized by category — complete with pricing ranges,
                  supplier verification notes, and quality check references.
                </p>
                <p className="text-base mt-3">
                  Think of it as your command center for China product research. Each entry in the spreadsheet
                  has been manually reviewed or sourced from verified transaction data, which means you are
                  looking at real products from real suppliers — not automated scrapes that might include
                  outdated or misleading listings.
                </p>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  Why Use a Spreadsheet for China Sourcing?
                </h2>
                <p className="text-base">
                  Most people start their China sourcing journey by simply searching on marketplaces directly.
                  While this works for casual browsing, it becomes inefficient when you need to compare dozens
                  of products across different platforms. A structured spreadsheet approach gives you several
                  key advantages:
                </p>
                <ul className="mt-4 space-y-3">
                  {[
                    {
                      title: "Side-by-side price comparison",
                      desc: "See prices from multiple suppliers for the same product category in one view. No more switching between tabs to compare costs.",
                    },
                    {
                      title: "Supplier verification at a glance",
                      desc: "Each supplier entry includes risk indicators and transaction history notes, so you can quickly filter out unreliable vendors.",
                    },
                    {
                      title: "Category-based organization",
                      desc: "Browse by Shoes, Hoodies, Jackets, Electronics, and more. The spreadsheet structure makes it easy to drill into specific product types.",
                    },
                    {
                      title: "Time efficiency",
                      desc: "What normally takes hours of searching across multiple sites can be done in minutes with a well-organized data set. For dropshippers managing multiple product lines, this time savings compounds quickly.",
                    },
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs"></i>
                      </div>
                      <div>
                        <strong className="text-foreground-900">{item.title}:</strong>{" "}
                        <span className="text-foreground-600">{item.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Section 3 - Categories */}
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  Product Categories Covered
                </h2>
                <p className="text-base">
                  The AllChinaBuySpreadsheet covers a wide range of product categories to serve different
                  types of buyers. From fashion and streetwear to accessories and lifestyle goods, the
                  database is organized to help you find exactly what you are looking for.
                </p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-md text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer"
                    >
                      <i className="ri-folder-line text-accent-500"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </section>

              {/* Section 4 - How to Use */}
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  How to Use AllChinaBuySpreadsheet: A Step-by-Step Approach
                </h2>
                <div className="space-y-5">
                  {[
                    {
                      step: "1",
                      title: "Choose Your Category",
                      desc: "Start by selecting the product category that matches what you want to source. The spreadsheet is organized into clear sections — Shoes, Apparel, Accessories, Electronics, and more. This narrows your focus immediately and prevents information overload.",
                    },
                    {
                      step: "2",
                      title: "Review Supplier Data",
                      desc: "Within each category, you will find supplier names alongside pricing ranges, minimum order quantities, and verification status. Pay attention to the risk scores — they are based on community feedback and transaction history, giving you a quick quality filter.",
                    },
                    {
                      step: "3",
                      title: "Compare Pricing Tiers",
                      desc: "Use the price comparison columns to identify which suppliers offer the best value for your target budget. The spreadsheet often includes both budget-friendly and premium-tier options, so you can match products to your specific market positioning.",
                    },
                    {
                      step: "4",
                      title: "Cross-Reference with External Platforms",
                      desc: "While the spreadsheet gives you a strong starting point, we always recommend cross-referencing supplier details on the actual marketplace platforms. This extra verification step adds a layer of confidence to your sourcing decisions.",
                    },
                    {
                      step: "5",
                      title: "Track and Update Regularly",
                      desc: "China product pricing and supplier availability can change frequently. The AllChinaBuySpreadsheet is updated regularly to reflect these changes, so checking back for the latest version ensures you are working with current data.",
                    },
                  ].map((item) => (
                    <div key={item.step} className="flex gap-4">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-semibold text-sm flex-shrink-0">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 5 - Benefits */}
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  Who Benefits Most from AllChinaBuySpreadsheet?
                </h2>
                <p className="text-base">
                  While anyone interested in China product sourcing can benefit from structured data, certain
                  groups find the AllChinaBuySpreadsheet particularly valuable:
                </p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      title: "Dropshippers",
                      desc: "Quickly identify trending products and reliable suppliers without spending days on manual research. Start testing new product lines faster.",
                      icon: "ri-send-plane-line",
                    },
                    {
                      title: "Sneaker Buyers",
                      desc: "Access batch comparisons, pricing tiers, and supplier notes specifically curated for the sneaker community. Find the best batches without guesswork.",
                      icon: "ri-footprint-line",
                    },
                    {
                      title: "Resellers",
                      desc: "Compare wholesale pricing across suppliers to maximize your margins. The price comparison tables are built for profit-focused decision making.",
                      icon: "ri-swap-line",
                    },
                    {
                      title: "Bulk Importers",
                      desc: "Identify suppliers that handle larger volumes with competitive pricing. Filter by minimum order quantities that match your business scale.",
                      icon: "ri-shopping-cart-2-line",
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="p-5 bg-background-100/70 border border-background-200/70 rounded-lg"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                          <i className={item.icon}></i>
                        </div>
                        <h3 className="font-heading text-base font-semibold text-foreground-900">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 6 - Tips */}
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  Pro Tips for Smarter Sourcing
                </h2>
                <div className="bg-background-100/70 border border-background-200/70 rounded-lg p-6 space-y-4">
                  {[
                    "Always verify supplier details independently before placing large orders — spreadsheet data is a starting point, not a guarantee.",
                    "Check update timestamps to ensure you are viewing the most recent version of the AllChinaBuySpreadsheet data.",
                    "Use the risk scoring system as a filter, but combine it with your own due diligence for the best results.",
                    "When comparing prices, factor in shipping costs and potential customs duties — the lowest product price does not always mean the best total cost.",
                    "Bookmark categories you frequently source from so you can quickly return to updated data without re-navigating the entire spreadsheet.",
                  ].map((tip, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0 mt-0.5">
                        <i className="ri-lightbulb-line text-xs"></i>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">{tip}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 7 - FAQ */}
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {[
                    {
                      q: "Is AllChinaBuySpreadsheet free to use?",
                      a: "The AllChinaBuySpreadsheet database is accessible through our main platform at kaspreadsheet.com. Pricing and access tiers may vary based on the level of detail and update frequency you require.",
                    },
                    {
                      q: "How often is the spreadsheet data updated?",
                      a: "Product listings, pricing, and supplier information are refreshed regularly — typically on a daily or weekly basis depending on the category. Check the update timestamp on the spreadsheet for the most recent refresh date.",
                    },
                    {
                      q: "Can I request specific products or categories to be added?",
                      a: "Yes. The AllChinaBuySpreadsheet team welcomes suggestions for new categories and products. Reach out through the main platform to submit your requests.",
                    },
                    {
                      q: "Do you guarantee product quality or supplier reliability?",
                      a: "No. As stated in our important notice, all information is for educational and informational purposes. We provide structured data to support your research, but we cannot guarantee individual supplier performance or product quality. Always conduct your own verification.",
                    },
                    {
                      q: "Is this only for clothing and sneakers?",
                      a: "While fashion and sneakers are popular categories, the spreadsheet covers a broad range of product types including electronics, accessories, bags, lifestyle goods, and more. Browse the full category list on our main platform.",
                    },
                  ].map((faq, i) => (
                    <details
                      key={i}
                      className="group border border-background-200/70 rounded-lg overflow-hidden"
                    >
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                        <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* Internal Links & CTA */}
              <section className="bg-background-100/70 border border-background-200/70 rounded-lg p-6 text-center">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                  Ready to Start Sourcing Smarter?
                </h2>
                <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
                  Explore the complete product categories and access the full AllChinaBuySpreadsheet
                  database on our main platform. Start making data-driven sourcing decisions today.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center px-6 py-2.5 text-sm font-semibold rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Open Spreadsheet Hub
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
                  >
                    <i className="ri-home-4-line"></i>
                    Return to Homepage
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}